import styled from '@emotion/styled';
import { Pencil } from 'lucide-react';
import { Team } from 'modules/teams/domain/interfaces/team';
import { TeamGroup } from 'modules/tournaments/domain/interfaces/team-group';
import { deleteStageTeamGroup, upsertStageTeamGroup } from 'modules/tournaments/store/stages/stages.actions';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store/store';
import UDButton from 'ud-ui/components/button';
import EmptyButton from 'ud-ui/components/empty-button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import { Table } from './stage-group-table';

const StateGroupWrapper = styled.div`
  margin-bottom: 40px;
`;

const EditButton = (props: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <EmptyButton {...props}>
      <Pencil size={16} />
    </EmptyButton>
  );
};

type StageGroupProps = {
  group: TeamGroup;
  teams: Team[];
};

const StageGroup = ({ group, teams }: StageGroupProps) => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [groupTeams, setGroupTeams] = useState<(Team | null)[]>(group.teams);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);

  const handleEditModeClick = useCallback(() => {
    setGroupTeams([...groupTeams, null]);

    setIsEditMode(true);
  }, [groupTeams]);

  const handleSaveClick = useCallback(() => {
    const newGroupTeams = groupTeams.filter((team) => !!team) as Team[];
    const teamIds = newGroupTeams.map((t) => t.id);
    setIsProcessing(true);

    dispatch(
      upsertStageTeamGroup({
        id: group.id,
        stageId: group.stageId,
        name: group.name,
        teamIds,
      }),
    ).then(() => {
      setGroupTeams(newGroupTeams);
      setIsEditMode(false);
      setIsProcessing(false);
    });
  }, [dispatch, group, groupTeams]);

  const handleRemoveTeam = useCallback(
    (index: number) => {
      setGroupTeams([...groupTeams.slice(0, index), ...groupTeams.slice(index + 1)]);
    },
    [groupTeams],
  );

  const handleTeamChange = useCallback(
    (index: number, teamId: number) => {
      const team = teams.find((team) => team.id === teamId);
      if (!team) {
        return;
      }

      const newTeams = groupTeams.map((item, i) => (i === index ? team : item));

      if (!groupTeams[index]) {
        newTeams.push(null);
      }

      setGroupTeams(newTeams);
    },
    [groupTeams, teams],
  );

  const handleDeleteStageGroupClick = useCallback(() => {
    setIsProcessing(true);
    dispatch(deleteStageTeamGroup(group.id)).then(() => {
      setIsProcessing(false);
    });
  }, [dispatch, group.id]);

  return (
    <StateGroupWrapper>
      <UDText className="mb-4 flex items-center" type="title-3">
        {group.name}

        {!isEditMode && <EditButton className="ml-4" onClick={handleEditModeClick} />}
      </UDText>

      <Table
        isEditMode={isEditMode}
        teams={groupTeams}
        stageTeams={teams}
        statistics={group.statistics}
        editClick={handleEditModeClick}
        saveClick={handleSaveClick}
        deleteClick={openDeleteModal}
        removeTeamClick={handleRemoveTeam}
        teamChange={handleTeamChange}
        isLoading={isProcessing}
      />

      <Modal show={isDeleteModalOpen} onClose={closeDeleteModal}>
        <Modal.Header title="Подтвердить удаление" subtitle="Вы действительно хотите удалить группу?" />
        <Modal.Footer justify="between">
          <UDButton variant="secondary" className="mb-3" type="button" onClick={closeDeleteModal} disabled={isProcessing} stretch>
            Отмена
          </UDButton>

          <UDButton
            variant="primary"
            className="mb-3"
            type="button"
            onClick={handleDeleteStageGroupClick}
            disabled={isProcessing}
            stretch
          >
            Удалить
          </UDButton>
        </Modal.Footer>
      </Modal>
    </StateGroupWrapper>
  );
};

export default StageGroup;
